import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, useDataProvider, useLocaleState, useTranslate } from "react-admin";

import {
  FetchSensoRawDataMutation,
  LocationTypeEnum,
  SensoRawDataBodyEncoding,
  SensoRawDataEndpoint,
} from "../generated/graphql";
import { space } from "../theme/designTokens";
import { dialogActionsSx, dialogContentSx, dialogTitleSx } from "../theme/dialogStyles";

/** The body is shown up to this many characters; the rest is cut. */
export const SENSO_RAW_DATA_DISPLAY_LIMIT = 200_000;

/** The largest value of a GraphQL Int. */
const MAX_PARAM_VALUE = 2_147_483_647;

/** The import's GET paths that may be shown as raw data. */
export const SENSO_RAW_DATA_PATHS: Record<string, SensoRawDataEndpoint> = {
  "/media4care/v1/haeuser": SensoRawDataEndpoint.Haeuser,
  "/media4care/v1/zimmer": SensoRawDataEndpoint.Zimmer,
  "/media4care/v1/bewohner": SensoRawDataEndpoint.Bewohner,
  "/media4care/v1/mitarbeiter": SensoRawDataEndpoint.Mitarbeiter,
  "/media4care/v1/masterdata": SensoRawDataEndpoint.Masterdata,
  "/media4care/v1/massnahmen": SensoRawDataEndpoint.Massnahmen,
};

/** The endpoint of a request row, or null when the request may not be shown. */
export const sensoRawDataEndpoint = (method: string, path: string | null): SensoRawDataEndpoint | null =>
  method === "GET" && path !== null && Object.hasOwn(SENSO_RAW_DATA_PATHS, path) ? SENSO_RAW_DATA_PATHS[path] : null;

type ParamNeed = "required" | "optional" | "none";

const PARAM_NEEDS: Record<SensoRawDataEndpoint, { haus: ParamNeed; bewohner: ParamNeed }> = {
  [SensoRawDataEndpoint.Haeuser]: { haus: "none", bewohner: "none" },
  [SensoRawDataEndpoint.Zimmer]: { haus: "required", bewohner: "none" },
  [SensoRawDataEndpoint.Bewohner]: { haus: "none", bewohner: "none" },
  [SensoRawDataEndpoint.Mitarbeiter]: { haus: "none", bewohner: "none" },
  [SensoRawDataEndpoint.Masterdata]: { haus: "none", bewohner: "none" },
  [SensoRawDataEndpoint.Massnahmen]: { haus: "optional", bewohner: "required" },
};

const KNOWN_ERRORS = [
  "not_configured",
  "unsupported",
  "credentials",
  "timeout",
  "unreachable",
  "tls",
  "http_error",
  "invalid_response",
  "too_large",
  "unknown",
];

const SHOWN_RESPONSE_HEADERS = ["content-type", "content-length", "date"];

type RawDataLocation = { type: LocationTypeEnum; externalId: string; name: string };
type RawDataPerson = { externalId: string; number?: string | null; displayName: string };

/** The part of the stored preview that the parameters are picked from. */
export type SensoRawDataPreview = { locations: RawDataLocation[]; patients: RawDataPerson[] };

type RawData = FetchSensoRawDataMutation["fetchSensoRawData"];

type Option = { value: string; label: string };

/** Only positive integers can be sent as parameters. */
const toParam = (externalId: string): number | null => {
  if (!/^[1-9]\d*$/.test(externalId)) return null;
  const value = Number(externalId);
  return value <= MAX_PARAM_VALUE ? value : null;
};

const toOptions = <T extends { externalId: string }>(items: T[], label: (item: T) => string): Option[] => {
  const seen = new Set<string>();
  return items.flatMap(item => {
    if (toParam(item.externalId) === null || seen.has(item.externalId)) return [];
    seen.add(item.externalId);
    return [{ value: item.externalId, label: label(item) }];
  });
};

type SensoRawDataDialogProps = {
  organizationId: string;
  endpoint: SensoRawDataEndpoint;
  method: string;
  path: string;
  preview: SensoRawDataPreview | null;
  onClose: () => void;
};

const NameValueList = ({ items }: { items: { name: string; value: string | number }[] }) => (
  <Box component="ul" sx={{ m: 0, pl: space.sm, fontFamily: "monospace", fontSize: "0.85em" }}>
    {items.map((item, index) => (
      // Names can repeat (headers), and the list never reorders.
      // eslint-disable-next-line react/no-array-index-key
      <li key={index}>
        {item.name}: {item.value}
      </li>
    ))}
  </Box>
);

/**
 * Fetches one SENSO endpoint on demand and shows the request and the response as they are.
 * The response holds resident data: it only lives in this component's state, and the parent
 * unmounts the dialog when it is closed.
 */
export const SensoRawDataDialog = ({
  organizationId,
  endpoint,
  method,
  path,
  preview,
  onClose,
}: SensoRawDataDialogProps) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const dataProvider = useDataProvider();
  const needs = PARAM_NEEDS[endpoint];
  const [hausId, setHausId] = useState("");
  const [bewohnerId, setBewohnerId] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RawData | null>(null);
  const [failed, setFailed] = useState(false);
  // Ignores a response that arrives after the dialog was closed.
  const mounted = useRef(true);
  const request = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const hausOptions = useMemo(
    () =>
      toOptions(
        (preview?.locations ?? []).filter(location => location.type === LocationTypeEnum.Site),
        location => location.name
      ),
    [preview]
  );
  const bewohnerOptions = useMemo(
    () =>
      toOptions(preview?.patients ?? [], person =>
        person.number ? `${person.displayName} (${person.number})` : person.displayName
      ),
    [preview]
  );

  const missingOptions =
    (needs.haus === "required" && hausOptions.length === 0) ||
    (needs.bewohner === "required" && bewohnerOptions.length === 0);
  const missingSelection =
    (needs.haus === "required" && hausId === "") || (needs.bewohner === "required" && bewohnerId === "");

  const clearResult = () => {
    request.current += 1;
    setResult(null);
    setFailed(false);
    setLoading(false);
  };

  const handleClose = () => {
    clearResult();
    onClose();
  };

  const handleLoad = async () => {
    request.current += 1;
    const current = request.current;
    setResult(null);
    setFailed(false);
    setLoading(true);
    try {
      const loaded: RawData = await dataProvider.fetchSensoRawData({
        organizationId,
        endpoint,
        hausId: needs.haus !== "none" && hausId !== "" ? Number(hausId) : undefined,
        bewohnerId: needs.bewohner !== "none" && bewohnerId !== "" ? Number(bewohnerId) : undefined,
      });
      if (mounted.current && current === request.current) setResult(loaded);
    } catch {
      if (mounted.current && current === request.current) setFailed(true);
    } finally {
      if (mounted.current && current === request.current) setLoading(false);
    }
  };

  const body = result?.body ?? null;
  const shownBody = useMemo(
    () =>
      body !== null && body.length > SENSO_RAW_DATA_DISPLAY_LIMIT ? body.slice(0, SENSO_RAW_DATA_DISPLAY_LIMIT) : body,
    [body]
  );
  const truncated = body !== null && shownBody !== body;
  const numbers = new Intl.NumberFormat(locale);

  const select = (
    name: string,
    label: string,
    value: string,
    options: Option[],
    onChange: (value: string) => void,
    optional: boolean
  ) => (
    <TextField
      select
      size="small"
      fullWidth
      label={label}
      value={value}
      disabled={loading || options.length === 0}
      onChange={event => {
        clearResult();
        onChange(event.target.value);
      }}
      inputProps={{ "data-testid": `senso-raw-data-${name}` }}
    >
      {optional && (
        <MenuItem value="">
          <em>{translate("integration.preview.rawData.noHaus")}</em>
        </MenuItem>
      )}
      {options.map(option => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );

  const errorMessage = (code: string) =>
    KNOWN_ERRORS.includes(code) ? translate(`integration.preview.rawData.error.${code}`) : code;

  const shownHeaders = (result?.responseHeaders ?? []).filter(header =>
    SHOWN_RESPONSE_HEADERS.includes(header.name.toLowerCase())
  );

  return (
    <Dialog open onClose={handleClose} fullWidth maxWidth="md" data-testid="senso-raw-data-dialog">
      <DialogTitle sx={dialogTitleSx}>
        {translate("integration.preview.rawData.title", { endpoint: `${method} ${path}` })}
      </DialogTitle>
      <DialogContent dividers sx={{ ...dialogContentSx, display: "flex", flexDirection: "column", gap: space.xs }}>
        <Typography variant="body2" color="text.secondary">
          {translate("integration.preview.rawData.description")}
        </Typography>
        {needs.haus !== "none" &&
          select(
            "haus",
            translate("integration.preview.rawData.haus"),
            hausId,
            hausOptions,
            setHausId,
            needs.haus === "optional"
          )}
        {needs.bewohner !== "none" &&
          select(
            "bewohner",
            translate("integration.preview.rawData.bewohner"),
            bewohnerId,
            bewohnerOptions,
            setBewohnerId,
            false
          )}
        {missingOptions && <Alert severity="info">{translate("integration.preview.rawData.noOptions")}</Alert>}
        {loading && (
          <Box role="status">
            <Typography variant="body2">{translate("integration.preview.rawData.loading")}</Typography>
            <LinearProgress sx={{ mt: space.xs }} />
          </Box>
        )}
        {failed && <Alert severity="error">{translate("integration.preview.rawData.failed")}</Alert>}
        {result?.error && <Alert severity="error">{errorMessage(result.error)}</Alert>}
        {result && !result.error && (
          <>
            <Box>
              <Typography variant="subtitle2">{translate("integration.preview.rawData.request")}</Typography>
              <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
                {result.method} {result.url}
              </Typography>
              {result.query.length > 0 && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {translate("integration.preview.rawData.query")}
                  </Typography>
                  <NameValueList items={result.query} />
                </>
              )}
              {result.requestHeaders.length > 0 && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {translate("integration.preview.rawData.headers")}
                  </Typography>
                  <NameValueList items={result.requestHeaders} />
                </>
              )}
            </Box>
            <Box>
              <Typography variant="subtitle2">{translate("integration.preview.rawData.response")}</Typography>
              {result.status !== null && result.status !== undefined && (
                <Typography variant="body2">
                  {translate("integration.preview.rawData.status", { status: result.status })}
                </Typography>
              )}
              {shownHeaders.length > 0 && <NameValueList items={shownHeaders} />}
              {shownBody === null ? (
                <Typography variant="body2" color="text.secondary">
                  {translate("integration.preview.rawData.noBody")}
                </Typography>
              ) : (
                <>
                  {result.bodyEncoding === SensoRawDataBodyEncoding.Base64 && (
                    <Alert severity="info" sx={{ mt: space.xs }}>
                      {translate("integration.preview.rawData.bodyBase64")}
                    </Alert>
                  )}
                  {truncated && (
                    <Alert severity="warning" sx={{ mt: space.xs }}>
                      {translate("integration.preview.rawData.truncated", {
                        bytes: numbers.format(result.bodyBytes ?? shownBody.length),
                      })}
                    </Alert>
                  )}
                  <Box
                    component="pre"
                    data-testid="senso-raw-data-body"
                    sx={{
                      mt: space.xs,
                      mb: 0,
                      p: space.xs,
                      maxHeight: 400,
                      overflow: "auto",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-all",
                      fontFamily: "monospace",
                      fontSize: "0.85em",
                      backgroundColor: "action.hover",
                    }}
                  >
                    {shownBody}
                  </Box>
                </>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={dialogActionsSx}>
        <Button
          color="primary"
          variant="contained"
          onClick={handleLoad}
          disabled={loading || missingOptions || missingSelection}
          label="integration.preview.rawData.load"
        />
        <Button color="secondary" variant="outlined" onClick={handleClose} label="ra.action.close" />
      </DialogActions>
    </Dialog>
  );
};
